import React, { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import Popover from "@mui/material/Popover";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";

import {
  API_report_email_subscription,
  API_report_email_subscription_stores,
} from "../../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../../requests/utilities/requests";
import { unwrapPayload } from "./reports/_shared/laborMath";
import { updateCachedEmailedReport } from "./reportEmailBadges";

const SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#6d28d9" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#6d28d9",
  },
  // While a save is in flight the switch is disabled; keep it a faded
  // violet instead of MUI's default (blue) disabled colors.
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": { color: "#c4b5fd" },
  "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
    backgroundColor: "#6d28d9",
    opacity: 0.3,
  },
};

const CHECKBOX_SX = { p: 0.5, color: "#94a3b8", "&.Mui-checked": { color: "#6d28d9" } };

// "Email me this" for Report Library reports that also go out by email.
// Reads and writes the same per-store email preference the delivery uses
// (on unless turned off). The switch covers every store the user would get
// it for; "Choose stores" sets them one at a time. This is the only place
// these preferences are managed (they're hidden from Notification Settings).

// Stores grouped for the picker: by company, then by district when the
// company has more than one (a null district reads as "No district").
export function groupStores(stores) {
  const companies = [];
  for (const store of stores || []) {
    let company = companies.find((c) => c.company_name === store.company_name);
    if (!company) {
      company = { company_name: store.company_name, districts: [] };
      companies.push(company);
    }
    const districtName = store.district_name || "No district";
    let district = company.districts.find((d) => d.district_name === districtName);
    if (!district) {
      district = { district_name: districtName, stores: [] };
      company.districts.push(district);
    }
    district.stores.push(store);
  }
  return companies;
}

export function subscriptionSummary(payload) {
  const total = payload?.stores?.length || 0;
  const enabled = payload?.enabled_count || 0;
  if (payload?.email_suppressed) {
    return "Email is unsubscribed for your address, so nothing is sent";
  }
  if (enabled === 0) return "Not emailed to you";
  if (total === 1) return "Emailed to you";
  return enabled === total
    ? `Emailed to you for all ${total} stores`
    : `Emailed to you for ${enabled} of ${total} stores`;
}

export default function ReportEmailToggle({ reportId, userId, schedule }) {
  const [payload, setPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_report_email_subscription, {
          report_id: reportId,
        });
        if (!cancelled) setPayload(unwrapPayload(res));
      } catch (e) {
        // Leave the toggle hidden; the report itself still works.
        if (!cancelled) setPayload(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  // Nothing to subscribe to: no store the user manages gets this email.
  if (!payload?.stores?.length) return null;

  const total = payload.stores.length;
  // On whenever the report reaches you for at least one store (like the
  // Emailed badge); which stores is in the tooltip and Choose stores.
  // Turning it off stops it for every store; turning it on starts it for all.
  const checked = payload.enabled_count > 0;

  // storeIds limits the change to those stores; omitted, it covers all.
  // Per-store changes skip the success toast so ticking boxes stays quiet.
  const save = async (enabled, storeIds) => {
    setSaving(true);
    try {
      // Per-store changes use their own endpoint: a server still on older
      // code 404s there instead of applying the change to every store.
      const res = storeIds
        ? await postRightworkAPI(API_report_email_subscription_stores, {
            report_id: reportId,
            enabled,
            store_ids: storeIds,
          })
        : await postRightworkAPI(API_report_email_subscription, {
            report_id: reportId,
            enabled,
          });
      const updated = unwrapPayload(res);
      setPayload(updated);
      // So the library's Emailed badge matches on the way back.
      // Applies even if the report has closed; ignored if another user has
      // since signed in (see setActiveEmailedReportsUser).
      updateCachedEmailedReport(userId, reportId, updated?.emailed);
      if (enabled && updated?.email_suppressed) {
        // The preference saved, but an unsubscribe, bounce, or complaint on
        // the address still blocks delivery; don't promise an email.
        toast.warning(
          "Saved, but email is unsubscribed for your address, so nothing will be sent.",
          { autoClose: 5000 }
        );
      } else if (!storeIds) {
        toast.success(
          enabled ? "You'll get this report by email." : "You won't get this report by email.",
          { autoClose: 3000 }
        );
      }
    } catch (e) {
      toast.error("Couldn't update your email settings. Try again.", { autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const tooltip = (
    <div className="space-y-1">
      <p>{subscriptionSummary(payload)}.</p>
      {schedule && <p>{schedule}</p>}
      {total > 1 && <p>Use Choose stores to pick which stores email it to you.</p>}
    </div>
  );
  const groups = groupStores(payload.stores);

  return (
    <div className="flex items-center gap-1 mr-3 prebuilt-report-no-print">
      <label className="flex items-center gap-1 cursor-pointer">
        <Switch
          size="small"
          checked={checked}
          disabled={saving}
          onChange={(event) => save(event.target.checked)}
          inputProps={{ "aria-label": "Email me this report" }}
          sx={SWITCH_SX}
        />
        <span className="text-sm text-slate-700 whitespace-nowrap">Email me this</span>
      </label>
      <Tooltip title={tooltip}>
        <InfoOutlinedIcon
          sx={{ fontSize: 16 }}
          className="text-slate-400 cursor-help"
          aria-label="About emailing this report"
        />
      </Tooltip>
      {total > 1 && (
        <button
          type="button"
          onClick={(event) => setPickerAnchor(event.currentTarget)}
          className="ml-1 text-xs text-violet-700 hover:underline whitespace-nowrap"
        >
          Choose stores
        </button>
      )}
      <Popover
        open={Boolean(pickerAnchor)}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            width: 320,
            maxWidth: "calc(100vw - 32px)",
          },
        }}
      >
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-900">Email it to me for</p>
            <div className="flex gap-3 text-xs">
              <button
                type="button"
                disabled={saving}
                onClick={() => save(true)}
                className="text-violet-700 hover:underline disabled:opacity-50"
              >
                Select all
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => save(false)}
                className="text-violet-700 hover:underline disabled:opacity-50"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto -mx-1">
            {groups.map((company) => (
              <div key={company.company_name} className="mb-2">
                {groups.length > 1 && (
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {company.company_name}
                  </p>
                )}
                {company.districts.map((district) => (
                  <div key={district.district_name}>
                    {company.districts.length > 1 && (
                      <p className="px-1 mt-1 text-[11px] text-slate-400">{district.district_name}</p>
                    )}
                    {district.stores.map((store) => (
                      <label
                        key={store.store_id}
                        className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          size="small"
                          checked={store.enabled}
                          disabled={saving}
                          onChange={(event) => save(event.target.checked, [store.store_id])}
                          sx={CHECKBOX_SX}
                        />
                        <span className="text-sm text-slate-700">{store.store_name}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Popover>
    </div>
  );
}
